/**
 * @tyndo/ui-kit/domain/ExceptionFirstToggle
 *
 * Closes G2.11 from docs/latest-analysis.md.
 *
 * A two-state segmented toggle every operational dashboard should
 * surface near its top header. "Show all" is the legacy view —
 * vanity metrics, charts, healthy + unhealthy mixed. "Show
 * exceptions only" promotes blocked work, stale data, missing
 * config, failed jobs ABOVE the routine throughput numbers, so
 * operators see what needs a human first.
 *
 * The component is presentation-only — the parent dashboard owns
 * the filtering. The toggle just emits state changes.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * const [mode, setMode] = useState<ExceptionFirstMode>('all');
 * const visibleRows = mode === 'exceptions'
 *   ? rows.filter(r => r.severity !== 'normal')
 *   : rows;
 *
 * <ExceptionFirstToggle
 *   value={mode}
 *   onChange={setMode}
 *   exceptionCount={rows.filter(r => r.severity !== 'normal').length}
 * />
 * ```
 */

import * as React from 'react';
import { AlertTriangle, List } from 'lucide-react';
import { Badge } from '../primitives/badge';

export type ExceptionFirstMode = 'all' | 'exceptions';

export interface ExceptionFirstToggleProps {
  value: ExceptionFirstMode;
  onChange: (next: ExceptionFirstMode) => void;
  /** Number of exceptions in the current data set; rendered as a badge. */
  exceptionCount: number;
  /** Optional total-row label (defaults to "all"). */
  allLabel?: string;
  /** Optional className for outer container. */
  className?: string;
}

export const ExceptionFirstToggle: React.FC<ExceptionFirstToggleProps> = ({
  value,
  onChange,
  exceptionCount,
  allLabel = 'All',
  className,
}) => {
  const isException = value === 'exceptions';
  return (
    <div
      role="radiogroup"
      aria-label="Exception-first toggle"
      className={[
        'inline-flex items-center rounded-md border border-border bg-surface-base p-0.5',
        className ?? '',
      ].join(' ')}
    >
      <button
        type="button"
        role="radio"
        aria-checked={!isException}
        onClick={() => onChange('all')}
        className={[
          'flex items-center gap-1.5 rounded px-2.5 py-1 text-sm transition-colors',
          !isException
            ? 'bg-card text-foreground shadow-xs'
            : 'text-muted-foreground hover:text-foreground',
        ].join(' ')}
      >
        <List size={14} />
        {allLabel}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={isException}
        onClick={() => onChange('exceptions')}
        className={[
          'flex items-center gap-1.5 rounded px-2.5 py-1 text-sm transition-colors',
          isException
            ? 'bg-card text-foreground shadow-xs'
            : 'text-muted-foreground hover:text-foreground',
        ].join(' ')}
      >
        <AlertTriangle size={14} className={isException ? 'text-alarm-major' : ''} />
        Exceptions
        {exceptionCount > 0 && (
          <Badge
            variant={exceptionCount > 0 && isException ? 'critical' : 'warning'}
            className="ml-0.5"
          >
            {exceptionCount}
          </Badge>
        )}
      </button>
    </div>
  );
};

ExceptionFirstToggle.displayName = 'ExceptionFirstToggle';
